import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { exportLabels } from '../../constants/exportLabels';
import { useDownloadExport, useExportTables } from '../../hooks/useDataExport';
import type { ExportGroup } from '../../types';

interface ExportCsvDialogProps {
  projectId: string;
  open: boolean;
  onClose: () => void;
}

const TITLE_ID = 'export-csv-dialog-title';

const GroupSection = ({
  group,
  selected,
  onToggle,
  onToggleGroup,
}: {
  group: ExportGroup;
  selected: ReadonlySet<string>;
  onToggle: (tableId: string) => void;
  onToggleGroup: (group: ExportGroup, checked: boolean) => void;
}) => {
  const chosen = group.tables.filter((table) => selected.has(table.id)).length;
  const note = group.status === 'stale' || group.status === 'missing' ? exportLabels.not_exported[group.status] : null;

  return (
    <Box component="section" aria-label={group.title}>
      <FormControlLabel
        label={<Typography fontWeight={600}>{group.title}</Typography>}
        disabled={group.tables.length === 0}
        control={
          <Checkbox
            checked={group.tables.length > 0 && chosen === group.tables.length}
            indeterminate={chosen > 0 && chosen < group.tables.length}
            onChange={(_, checked) => onToggleGroup(group, checked)}
          />
        }
      />
      {note && (
        <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
          {note}
        </Typography>
      )}
      <Stack sx={{ ml: 4 }}>
        {group.tables.map((table) => (
          <FormControlLabel
            key={table.id}
            label={
              <Typography variant="body2">
                {table.title}{' '}
                <Typography component="span" variant="body2" color="text.secondary">
                  ({exportLabels.rows(table.row_count)})
                </Typography>
              </Typography>
            }
            control={<Checkbox size="small" checked={selected.has(table.id)} onChange={() => onToggle(table.id)} />}
          />
        ))}
      </Stack>
    </Box>
  );
};

const ExportCsvDialog = ({ projectId, open, onClose }: ExportCsvDialogProps) => {
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const { data, isLoading, isError } = useExportTables(projectId, open);
  const download = useDownloadExport(projectId);
  const allIds = useMemo(() => (data?.groups ?? []).flatMap((group) => group.tables.map((table) => table.id)), [data]);
  // Tables of a package that went stale since the last time are dropped from the choice.
  const chosen = useMemo(() => allIds.filter((id) => selected.has(id)), [allIds, selected]);

  const toggle = (tableId: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (!next.delete(tableId)) next.add(tableId);
      return next;
    });
  };

  const toggleGroup = (group: ExportGroup, checked: boolean) => {
    setSelected((current) => {
      const next = new Set(current);
      group.tables.forEach((table) => (checked ? next.add(table.id) : next.delete(table.id)));
      return next;
    });
  };

  const handleDownload = () => {
    download.mutate({ format: 'csv', tables: chosen }, { onSuccess: onClose });
  };

  return (
    <Dialog open={open} onClose={download.isPending ? undefined : onClose} aria-labelledby={TITLE_ID} fullWidth maxWidth="sm">
      <DialogTitle id={TITLE_ID}>{exportLabels.csv_title}</DialogTitle>
      <DialogContent dividers>
        <DialogContentText sx={{ mb: 2 }}>{exportLabels.csv_intro}</DialogContentText>
        {isLoading && (
          <Stack direction="row" spacing={1} alignItems="center" role="status">
            <CircularProgress size={18} />
            <Typography variant="body2">{exportLabels.csv_loading}</Typography>
          </Stack>
        )}
        {isError && <Alert severity="error">{exportLabels.csv_load_error}</Alert>}
        {data && (
          <Stack spacing={1}>
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={() => setSelected(new Set(allIds))}>
                {exportLabels.csv_select_all}
              </Button>
              <Button size="small" onClick={() => setSelected(new Set())}>
                {exportLabels.csv_clear}
              </Button>
            </Stack>
            {data.groups.map((group) => (
              <GroupSection key={group.key} group={group} selected={selected} onToggle={toggle} onToggleGroup={toggleGroup} />
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={download.isPending}>
          {exportLabels.csv_cancel}
        </Button>
        <Button
          variant="contained"
          onClick={handleDownload}
          disabled={chosen.length === 0 || download.isPending}
          loading={download.isPending}
        >
          {exportLabels.csv_download(chosen.length)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportCsvDialog;
